import React, { useState, useEffect } from 'react';

// RAG 파이프라인 시각화 + 인라인 편집 (저장 버튼 클릭 시 일괄 반영)
// 후보 수 = 방향별 기준(Top K=의미기반 / Top D=BM25) × 출처별 배수(교육/공통)
// props: cfg(저장된 설정), onSave(newCfg)
const ACC = {
  io: '#64748b', proc: '#3b82f6', vec: '#4A90D9', bm: '#F0A500',
  db: '#0ea5e9', rrf: '#9B59B6', rank: '#E67E22', gen: '#22c55e',
};
const num = (v, d) => { const n = Number(v); return Number.isFinite(n) ? n : d; };

function Node({ cx, cy, w, h, title, sub, accent, shape }) {
  const x = cx - w / 2, y = cy - h / 2;
  return (
    <g>
      {shape === 'db' ? (
        <>
          <path d={`M${x},${y + 12} A${w / 2},12 0 0 1 ${x + w},${y + 12} L${x + w},${y + h - 12} A${w / 2},12 0 0 1 ${x},${y + h - 12} Z`} fill="#fff" stroke={accent} strokeWidth="2" />
          <path d={`M${x},${y + 12} A${w / 2},12 0 0 0 ${x + w},${y + 12}`} fill="#eaf6fe" stroke={accent} strokeWidth="2" />
        </>
      ) : (
        <>
          <rect x={x} y={y} width={w} height={h} rx="12" fill="#fff" stroke="#e2e8f0" strokeWidth="1.5" />
          <rect x={x} y={y} width="6" height={h} rx="3" fill={accent} />
        </>
      )}
      <circle cx={cx} cy={y} r="4" fill="#fff" stroke={accent} strokeWidth="2" />
      <circle cx={cx} cy={y + h} r="4" fill="#fff" stroke={accent} strokeWidth="2" />
      {/* DB 실린더는 윗 뚜껑(타원)을 피해 텍스트를 본체로 내린다 */}
      <text x={cx} y={cy + (shape === 'db' ? 4 : -9)} textAnchor="middle" dominantBaseline="middle" fontSize="16" fontWeight="700" fill="#1f2937">{title}</text>
      <text x={cx} y={cy + (shape === 'db' ? 24 : 13)} textAnchor="middle" dominantBaseline="middle" fontSize="11" fill="#64748b">{sub}</text>
    </g>
  );
}
function Edge({ x1, y1, x2, y2, color = '#b1b1b7', curve = 0 }) {
  const dy = y2 - y1, c1y = y1 + dy * 0.45, c2y = y2 - dy * 0.45;
  const d = curve
    ? `M${x1},${y1} C${(x1 + x2) / 2 + curve},${c1y} ${(x1 + x2) / 2 + curve},${c2y} ${x2},${y2}`
    : `M${x1},${y1} C${x1},${c1y} ${x2},${c2y} ${x2},${y2}`;
  return <path d={d} fill="none" stroke={color} strokeWidth="2.2" markerEnd="url(#ar)" />;
}
function StaticLabel({ x, y, text, color = '#334155' }) {
  const kor = [...text].filter(ch => ch.charCodeAt(0) > 0x1100).length;
  const w = 22 + kor * 13 + (text.length - kor) * 7;
  return (
    <g>
      <rect x={x - w / 2} y={y - 15} width={w} height="30" rx="15" fill="#fff" stroke={color} strokeWidth="1.3" />
      <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="middle" fontSize="12.5" fontWeight="600" fill={color}>{text}</text>
    </g>
  );
}
function Chip({ cx, cy, w, prefix, value, onChange, suffix }) {
  return (
    <foreignObject x={cx - w / 2} y={cy - 16} width={w} height={32}>
      <div xmlns="http://www.w3.org/1999/xhtml" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, height: '100%',
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: '0 8px', boxSizing: 'border-box',
        fontFamily: 'Apple SD Gothic Neo, sans-serif', whiteSpace: 'nowrap', overflow: 'hidden',
      }}>
        {prefix && <span style={{ fontSize: 11.5, color: '#475569' }}>{prefix}</span>}
        <input type="number" className="ragchip-num" value={value} onChange={onChange} style={{
          width: 46, padding: '2px 2px', textAlign: 'center', fontSize: 12.5, fontWeight: 700,
          color: '#1d4ed8', background: '#eff6ff', border: '1.6px solid #2563eb', borderRadius: 6,
        }} />
        {suffix && <span style={{ fontSize: 11, color: '#94a3b8' }}>{suffix}</span>}
      </div>
    </foreignObject>
  );
}

const FIELDS = [
  { key: 'top_k_vec', label: '의미기반 기준 후보 (Top K)', step: 1, help: '의미기반(벡터) 검색의 기준 후보 수' },
  { key: 'top_d_bm25', label: 'BM25 기준 후보 (Top D)', step: 1, help: 'BM25 검색의 기준 후보 수' },
  { key: 'course_mult', label: '교육 후보 배수', step: 1, help: '교육 후보 = Top × 이 배수' },
  { key: 'common_mult', label: '공통 후보 배수', step: 1, help: '공통 후보 = Top × 이 배수' },
  { key: 'rerank_n', label: '리랭크 후보 수', step: 1, help: '리랭크에 넘길 후보 수' },
  { key: 'top_k', label: '답변에 사용할 자료 수', step: 1, help: '최종 답변 근거로 쓸 자료 수' },
  { key: 'threshold', label: '관련도 기준', step: 0.1, help: '낮을수록 엄격 (덜 관련된 자료 제외)' },
  { key: 'weight_course', label: '교육 우선도', step: 0.1, help: '높을수록 교육 자료 우선' },
  { key: 'weight_common', label: '공통 우선도', step: 0.1, help: '높을수록 공통 자료 우선' },
  { key: 'weight_vector', label: '의미기반 우선도', step: 0.1, help: '의미기반 검색 가중치' },
  { key: 'weight_bm25', label: 'BM25 우선도', step: 0.1, help: 'BM25 검색 가중치' },
  { key: 'temperature', label: '답변 창의성', step: 0.1, help: '0=사실 위주, 높을수록 다양함' },
];
const ADVANCED = [
  { key: 'rrf_k', label: 'RRF K (결합 상수)', step: 1, help: '두 검색 결과 결합 시 순위 완충값. 보통 60' },
];

export default function RagPipeline({ cfg, onSave }) {
  const [draft, setDraft] = useState(cfg || {});
  const [panelOpen, setPanelOpen] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  useEffect(() => { setDraft(cfg || {}); }, [cfg]);

  const set = (k, v) => setDraft(prev => ({ ...prev, [k]: v === '' ? '' : Number(v) }));
  const onIn = (k) => (e) => set(k, e.target.value);
  const d = draft;
  const dirty = JSON.stringify(draft) !== JSON.stringify(cfg || {});

  const K = num(d.top_k_vec, 10), D = num(d.top_d_bm25, 10);
  const cm = num(d.course_mult, 2), nm = num(d.common_mult, 1);
  const dz = (a, b) => (a === b ? `= ${a}개` : `의미${a}·단어${b}`);
  const courseHint = dz(Math.max(1, K * cm), Math.max(1, D * cm));
  const commonHint = dz(Math.max(1, K * nm), Math.max(1, D * nm));

  const cx = 650, yQ = 78, yP = 182, yN = 292, yS = 460, yDB = 648, yR = 826, yRR = 988, yG = 1108;
  const xVEC = 380, xBM = 920, xC1 = 495, xC2 = 805, wN = 270, hN = 64;
  const mSDB = (yS + yDB) / 2, mDBR = (yDB + yR) / 2;
  const rrfX = cx - 320, rrfY = yR - 58, rrfW = 640, rrfH = 116;
  const bx = 36, by = 56, bw = 340, bh = 86;
  const G = 9; // 화살표 꼭지와 노드 사이 간격

  const renderRow = (f) => (
    <div key={f.key} className="mb-3">
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs font-semibold text-gray-700 flex-1">{f.label}</label>
        <input type="number" step={f.step} value={d[f.key] ?? ''} onChange={onIn(f.key)}
               className="w-20 p-1.5 border border-gray-300 rounded text-sm text-right" />
      </div>
      <p className="text-[11px] text-gray-400 mt-0.5">{f.help}</p>
    </div>
  );

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <style>{`
        .ragchip-num::-webkit-outer-spin-button,
        .ragchip-num::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .ragchip-num { -moz-appearance: textfield; appearance: textfield; }
      `}</style>
      <div className="flex items-center gap-3 mb-3">
        <button onClick={() => setPanelOpen(o => !o)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50">
          {panelOpen ? '◀ 설정 패널 닫기' : '☰ 설정 패널 (전체 목록)'}
        </button>
        <span className="text-xs text-gray-500">파란 칸을 직접 고치고 <b>저장</b>하면 챗봇 검색 방식이 바뀝니다.</span>
        {dirty && <span className="text-xs font-bold text-amber-600">● 변경됨</span>}
        <div className="flex-1" />
        <button onClick={() => setDraft(cfg || {})} disabled={!dirty}
                className={`px-3 py-1.5 text-sm rounded-md border ${dirty ? 'border-gray-300 hover:bg-gray-50' : 'border-gray-200 text-gray-300 cursor-not-allowed'}`}>되돌리기</button>
        <button onClick={() => onSave(draft)} disabled={!dirty}
                className={`px-5 py-1.5 text-sm rounded-md font-bold text-white ${dirty ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>저장</button>
      </div>

      <div className="flex-1 flex gap-4 overflow-hidden">
        {panelOpen && (
          <div className="w-72 shrink-0 bg-white rounded-lg shadow-sm border border-gray-200 p-4 overflow-y-auto">
            <h3 className="font-bold text-gray-800 mb-3">검색 설정 전체</h3>
            {FIELDS.map(renderRow)}
            <button onClick={() => setAdvOpen(o => !o)} className="text-xs text-blue-600 mt-1 mb-2">
              {advOpen ? '▲ 고급 설정 닫기' : '▼ 고급 설정'}
            </button>
            {advOpen && ADVANCED.map(renderRow)}
          </div>
        )}

        <div className="flex-1 bg-[#f8fafc] rounded-lg border border-gray-200 overflow-auto">
          <svg viewBox="0 0 1300 1180" width="100%" style={{ minWidth: 840, fontFamily: 'Apple SD Gothic Neo, sans-serif' }}>
            <defs>
              <pattern id="grid" width="22" height="22" patternUnits="userSpaceOnUse">
                <circle cx="1.2" cy="1.2" r="1.2" fill="#dbe2ea" />
              </pattern>
              <marker id="ar" markerWidth="10" markerHeight="10" refX="7" refY="3.2" orient="auto">
                <path d="M0,0 L7,3.2 L0,6.4 Z" fill="#b1b1b7" />
              </marker>
            </defs>
            <rect width="1300" height="1180" fill="url(#grid)" />

            {/* 엣지 (G=화살표 꼭지 간격) */}
            <Edge x1={cx} y1={yQ + hN / 2} x2={cx} y2={yP - hN / 2 - G} />
            <Edge x1={cx} y1={yP + hN / 2} x2={cx} y2={yN - hN / 2 - G} />
            <Edge x1={cx} y1={yN + hN / 2} x2={xVEC} y2={yS - hN / 2 - G} color={ACC.vec} />
            <Edge x1={cx} y1={yN + hN / 2} x2={xBM} y2={yS - hN / 2 - G} color={ACC.bm} />
            <Edge x1={xVEC} y1={yS + hN / 2} x2={xC1} y2={yDB - 40 - G} color={ACC.vec} />
            <Edge x1={xVEC} y1={yS + hN / 2} x2={xC2} y2={yDB - 40 - G} color={ACC.vec} curve={40} />
            <Edge x1={xBM} y1={yS + hN / 2} x2={xC1} y2={yDB - 40 - G} color={ACC.bm} curve={-40} />
            <Edge x1={xBM} y1={yS + hN / 2} x2={xC2} y2={yDB - 40 - G} color={ACC.bm} />
            <Edge x1={xC1} y1={yDB + 40} x2={cx - 30} y2={rrfY - G} />
            <Edge x1={xC2} y1={yDB + 40} x2={cx + 30} y2={rrfY - G} />
            <Edge x1={cx} y1={rrfY + rrfH} x2={cx} y2={yRR - hN / 2 - G} />
            <Edge x1={cx} y1={yRR + hN / 2} x2={cx} y2={yG - hN / 2 - G} />

            <StaticLabel x={cx - 150} y={(yN + yS) / 2} text="의미 기반" color={ACC.vec} />
            <StaticLabel x={cx + 170} y={(yN + yS) / 2} text="키워드(BM25)" color={ACC.bm} />

            {/* 노드 */}
            <Node cx={cx} cy={yQ} w={wN} h={hN} title="사용자 질문" sub="방문자 입력" accent={ACC.io} />
            <Node cx={cx} cy={yP} w={wN} h={hN} title="개인정보 마스킹" sub="전화번호·이름 가림" accent={ACC.proc} />
            <Node cx={cx} cy={yN} w={wN} h={hN} title="동의어 정규화" sub="같은 뜻 표현 통일" accent={ACC.proc} />
            <Node cx={xVEC} cy={yS} w={wN} h={hN} title="의미기반 검색" sub="임베딩 유사도(pgvector)" accent={ACC.vec} />
            <Node cx={xBM} cy={yS} w={wN} h={hN} title="BM25 검색" sub="키워드 일치" accent={ACC.bm} />
            <Node cx={xC1} cy={yDB} w={250} h={80} title="교육 자료" sub="선택한 교육 컬렉션" accent={ACC.db} shape="db" />
            <Node cx={xC2} cy={yDB} w={250} h={80} title="공통 자료" sub="__common__ 컬렉션" accent={ACC.db} shape="db" />
            <Node cx={cx} cy={yRR} w={wN} h={hN} title="리랭크" sub="AI 관련도 재정렬(gpt-4o-mini)" accent={ACC.rank} />
            <Node cx={cx} cy={yG} w={wN + 20} h={hN} title="답변 생성" sub="gpt-4o-mini" accent={ACC.gen} />

            {/* RRF 결합 노드 */}
            <rect x={rrfX} y={rrfY} width={rrfW} height={rrfH} rx="14" fill="#fff" stroke="#e2e8f0" strokeWidth="1.5" />
            <rect x={rrfX} y={rrfY} width="6" height={rrfH} rx="3" fill={ACC.rrf} />
            <text x={cx} y={rrfY + 25} textAnchor="middle" fontSize="16" fontWeight="700" fill="#1f2937">RRF 결합</text>
            <text x={cx} y={rrfY + 44} textAnchor="middle" fontSize="11" fill="#64748b">의미기반·BM25 결과를 점수로 합쳐 정렬 · 아래 우선도 조절 (출처: 교육·공통 / 검색방법: 의미기반·BM25)</text>

            {/* 좌상단 컨트롤 박스 */}
            <rect x={bx} y={by} width={bw} height={bh} rx="12" fill="#fff" stroke="#2563eb" strokeWidth="1.6" />
            <text x={bx + 16} y={by + 24} fontSize="13" fontWeight="800" fill="#0f2740">기준 후보 수 (Top)</text>
            <text x={bx + 16} y={by + 42} fontSize="10.5" fill="#64748b">아래 K·D × 배수 = 실제 후보 수</text>
            <Chip cx={bx + 100} cy={by + 66} w={120} prefix="Top K" value={d.top_k_vec ?? ''} onChange={onIn('top_k_vec')} />
            <Chip cx={bx + 250} cy={by + 66} w={120} prefix="Top D" value={d.top_d_bm25 ?? ''} onChange={onIn('top_d_bm25')} />

            {/* 후보 배수 (교육/공통 1개씩) */}
            <Chip cx={xC1} cy={mSDB + 6} w={186} prefix="교육 후보 ×" value={d.course_mult ?? ''} onChange={onIn('course_mult')} suffix={courseHint} />
            <Chip cx={xC2} cy={mSDB + 6} w={186} prefix="공통 후보 ×" value={d.common_mult ?? ''} onChange={onIn('common_mult')} suffix={commonHint} />

            {/* 관련도 기준 (양쪽 같은 높이) */}
            <Chip cx={xC1 - 6} cy={mDBR} w={128} prefix="관련도 기준" value={d.threshold ?? ''} onChange={onIn('threshold')} />
            <Chip cx={xC2 + 6} cy={mDBR} w={128} prefix="관련도 기준" value={d.threshold ?? ''} onChange={onIn('threshold')} />

            {/* 리랭크 후보 / 답변 수 */}
            <Chip cx={cx} cy={(rrfY + rrfH + yRR - hN / 2) / 2} w={150} prefix="리랭크 후보" value={d.rerank_n ?? ''} onChange={onIn('rerank_n')} suffix="개" />
            <Chip cx={cx} cy={(yRR + yG) / 2} w={150} prefix="답변에 사용" value={d.top_k ?? ''} onChange={onIn('top_k')} suffix="개" />

            {/* 우선도 (4개 모두 박스 안) */}
            <Chip cx={rrfX + 80} cy={rrfY + 90} w={130} prefix="교육 우선" value={d.weight_course ?? ''} onChange={onIn('weight_course')} />
            <Chip cx={rrfX + 240} cy={rrfY + 90} w={130} prefix="공통 우선" value={d.weight_common ?? ''} onChange={onIn('weight_common')} />
            <Chip cx={rrfX + 400} cy={rrfY + 90} w={130} prefix="의미기반" value={d.weight_vector ?? ''} onChange={onIn('weight_vector')} />
            <Chip cx={rrfX + 560} cy={rrfY + 90} w={130} prefix="BM25" value={d.weight_bm25 ?? ''} onChange={onIn('weight_bm25')} />

            {/* 답변 창의성 */}
            <Chip cx={cx + 220} cy={yG} w={140} prefix="답변 창의성" value={d.temperature ?? ''} onChange={onIn('temperature')} />
          </svg>
        </div>
      </div>
    </div>
  );
}
